import type {
  Agent,
  CodexAccount,
  CodexAccountMode,
  CodexAccountState,
  CodexAvailability,
} from './apiModels'

export const CODEX_ACCOUNT_LABELS: Record<CodexAccount, string> = {
  primary: 'Codex 1',
  secondary: 'Codex 2',
}

export const CODEX_ACCOUNT_MODE_OPTIONS: Array<{
  value: CodexAccountMode
  label: string
  detail: string
}> = [
  { value: 'auto', label: 'Auto', detail: 'Follow usage limits: fail over to the other login, fall back when it resets.' },
  { value: 'primary', label: 'Codex 1', detail: 'Pin every Codex turn to the Pro login, even past its limit (paid credits).' },
  { value: 'secondary', label: 'Codex 2', detail: 'Pin every Codex turn to the second login.' },
]

/** The login a chat/topic asks for, from its model key. */
export function requestedCodexAccount(agent: Agent | null | undefined): CodexAccount | null {
  if (agent === 'codex') return 'primary'
  if (agent === 'codex-secondary') return 'secondary'
  return null
}

export type CodexAccountChip = {
  /** Short header text, e.g. "Codex 2 · failover". */
  label: string
  tone: 'blue' | 'amber' | 'stone'
  description: string
}

/**
 * Header chip for a Codex conversation. Rendered only when the login that
 * will run the next turn is NOT the plain Codex 1 default the row asked for,
 * so the common case stays quiet and a switch is impossible to miss.
 */
export function codexAccountChip(
  agent: Agent | null | undefined,
  availability: CodexAvailability | null | undefined,
): CodexAccountChip | null {
  const requested = requestedCodexAccount(agent)
  if (!requested || !availability) return null
  const resolution = availability.effective[requested]
  if (!resolution) return null
  const effectiveLabel = CODEX_ACCOUNT_LABELS[resolution.account]
  if (resolution.reason === 'failover') {
    const from = CODEX_ACCOUNT_LABELS[requested]
    const blocked = availability.accounts.find((a) => a.account === requested)
    return {
      label: `${effectiveLabel} · failover`,
      tone: 'amber',
      description: `${from} hit its usage limit${formatResetSuffix(blocked)}; turns run on ${effectiveLabel} until it resets.`,
    }
  }
  if (resolution.reason === 'pinned') {
    return {
      label: `${effectiveLabel} · pinned`,
      tone: 'blue',
      description: `Codex account mode is pinned to ${effectiveLabel} (Settings → Codex account).`,
    }
  }
  if (resolution.reason === 'exhausted') {
    return {
      label: `${effectiveLabel} · both limited`,
      tone: 'amber',
      description: 'Both Codex logins are at their usage limit; turns stay on the requested login (Codex 1 spends paid credits).',
    }
  }
  if (requested === 'secondary') {
    return {
      label: effectiveLabel,
      tone: 'stone',
      description: 'This conversation runs on the second Codex login.',
    }
  }
  return null
}

export function formatResetTime(epoch: number | null | undefined): string {
  if (!epoch) return 'unknown'
  const d = new Date(epoch * 1000)
  if (Number.isNaN(d.getTime())) return 'unknown'
  return d.toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatResetSuffix(state: CodexAccountState | undefined): string {
  if (!state?.restricted_until) return ''
  return ` (resets ${formatResetTime(state.restricted_until)})`
}

/** One-line usage summary for the Settings card, e.g. "Pro · weekly 100% used". */
export function codexAccountUsageSummary(state: CodexAccountState): string {
  const bits: string[] = []
  const usage = state.usage
  if (usage?.plan) bits.push(usage.plan.charAt(0).toUpperCase() + usage.plan.slice(1))
  if (usage?.seven_day_used_pct != null) bits.push(`weekly ${Math.round(usage.seven_day_used_pct)}% used`)
  if (usage?.five_hour_used_pct != null) bits.push(`5-hour ${Math.round(usage.five_hour_used_pct)}% used`)
  if (usage && !usage.available) bits.push('usage read failed')
  if (state.restricted) {
    bits.push(`limited until ${formatResetTime(state.restricted_until)}`)
  } else if (bits.length === 0) {
    bits.push('no usage reading yet')
  }
  return bits.join(' · ')
}
